import React from 'react';

// To add new icons:
// 1. Add the `NewIcon.svg` file inside the `icons` folder.
// 2. Add the following import in this file: `import { ReactComponent as NewIcon } from "./NewIcon.svg";`
// 3. Add a new `NewIcon` entry to the `icons` dictionary, wrapping the new icon with the `iconWrapper` function: `NewIcon: iconWrapper("NewIcon", NewIcon),`
import { ReactComponent as Actions } from './Actions.svg';
import { ReactComponent as ActivityFeed } from './ActivityFeed.svg';
import { ReactComponent as AddedDoc } from './AddedDoc.svg';
import { ReactComponent as AddMembers } from './AddMembers.svg';
import { ReactComponent as AddPeople } from './AddPeople.svg';
import { ReactComponent as AddPerson } from './AddPerson.svg';
import { ReactComponent as ArrowUp } from './ArrowUp.svg';
import { ReactComponent as ArrowLeft } from './ArrowLeft.svg';
import { ReactComponent as ArrowReturn } from './ArrowReturn.svg';
import { ReactComponent as ArrowShortLeft } from './ArrowShortLeft.svg';
import { ReactComponent as Bell } from './Bell.svg';
import { ReactComponent as Chat } from './Chat.svg';
import { ReactComponent as Checked } from './Checked.svg';
import { ReactComponent as CheckedFilled } from './CheckedFilled.svg';
import { ReactComponent as Checkmark } from './Checkmark.svg';
import { ReactComponent as CheckmarkRounded } from './CheckmarkRounded.svg';
import { ReactComponent as ChevronThick } from './ChevronThick.svg';
import { ReactComponent as ChevronThinDown } from './ChevronThinDown.svg';
import { ReactComponent as Close } from './Close.svg';
import { ReactComponent as CloseCircle } from './CloseCircle.svg';
import { ReactComponent as CloseOutline } from './CloseOutline.svg';
import { ReactComponent as CollectionFilled } from './CollectionFilled.svg';
import { ReactComponent as Collections } from './Collections.svg';
import { ReactComponent as Copy } from './Copy.svg';
import { ReactComponent as DocumentFilled } from './DocumentFilled.svg';
import { ReactComponent as Download } from './Download.svg';
import { ReactComponent as Edit } from './Edit.svg';
import { ReactComponent as EmptyCollections } from './EmptyCollections.svg';
import { ReactComponent as EmptyNotifications } from './EmptyNotifications.svg';
import { ReactComponent as Exclamation } from './Exclamation.svg';
import { ReactComponent as Folder } from './Folder.svg';
import { ReactComponent as Gear } from './Gear.svg';
import { ReactComponent as Home } from './Home.svg';
import { ReactComponent as HomeFilled } from './HomeFilled.svg';
import { ReactComponent as Integrations } from './Integrations.svg';
import { ReactComponent as IntegrationsFilled } from './IntegrationsFilled.svg';
import { ReactComponent as List } from './List.svg';
import { ReactComponent as ListFilled } from './ListFilled.svg';
import { ReactComponent as ListFilled16 } from './ListFilled16.svg';
import { ReactComponent as ListSave } from './ListSave.svg';
import { ReactComponent as LockClosed } from './LockClosed.svg';
import { ReactComponent as LockOpen } from './LockOpen.svg';
import { ReactComponent as Member } from './Member.svg';
import { ReactComponent as Members } from './Members.svg';
import { ReactComponent as Menu } from './Menu.svg';
import { ReactComponent as MenuClose } from './MenuClose.svg';
import { ReactComponent as MyDocuments } from './MyDocuments.svg';
import { ReactComponent as OpenAll } from './OpenAll.svg';
import { ReactComponent as PeopleList } from './PeopleList.svg';
import { ReactComponent as Pin } from './Pin.svg';
import { ReactComponent as Plus } from './Plus.svg';
import { ReactComponent as PlusBox } from './PlusBox.svg';
import { ReactComponent as PlusBoxFilled } from './PlusBoxFilled.svg';
import { ReactComponent as PlusCircle } from './PlusCircle.svg';
import { ReactComponent as PlusCircleChecked } from './PlusCircleChecked.svg';
import { ReactComponent as PlusCircleDark } from './PlusCircleDark.svg';
import { ReactComponent as ProfileAvatar } from './ProfileAvatar.svg';
import { ReactComponent as Search } from './Search.svg';
import { ReactComponent as Share } from './Share.svg';
import { ReactComponent as ShareFilled } from './ShareFilled.svg';
import { ReactComponent as StarFilled } from './StarFilled.svg';
import { ReactComponent as Tabs } from './Tabs.svg';
import { ReactComponent as Trash } from './Trash.svg';
import { ReactComponent as TriangleDown } from './TriangleDown.svg';
import { ReactComponent as UserSwitch } from './UserSwitch.svg';
import { ReactComponent as WorkingDoc } from './WorkingDoc.svg';

import classnames from 'classnames';
import styles from './icons.module.scss';

/**
 * Returns a function component that renders the correct svg
 * @param iconName
 * @param Icon
 */
function iconWrapper(iconName: string, Icon: React.ElementType): Icon {
  return ({
    className, // eslint-disable-line
    textPosition, // eslint-disable-line
    ...props
  }: IconWrapperProps) => {
    const classes = classnames(className, styles[iconName], styles[`textPosition-${textPosition}`]);
    return <Icon className={classes} {...props} />;
  };
}

/**
 * Available Icons Map
 */
const icons: IconList = {
  Actions: iconWrapper('Actions', Actions),
  ActivityFeed: iconWrapper('ActivityFeed', ActivityFeed),
  AddedDoc: iconWrapper('AddedDoc', AddedDoc),
  AddMembers: iconWrapper('AddMembers', AddMembers),
  AddPeople: iconWrapper('AddPeople', AddPeople),
  AddPerson: iconWrapper('AddPerson', AddPerson),
  ArrowUp: iconWrapper('ArrowUp', ArrowUp),
  ArrowLeft: iconWrapper('ArrowLeft', ArrowLeft),
  ArrowReturn: iconWrapper('ArrowReturn', ArrowReturn),
  ArrowShortLeft: iconWrapper('ArrowShortLeft', ArrowShortLeft),
  Bell: iconWrapper('Bell', Bell),
  Chat: iconWrapper('Chat', Chat),
  Checked: iconWrapper('Checked', Checked),
  CheckedFilled: iconWrapper('CheckedFilled', CheckedFilled),
  Checkmark: iconWrapper('Checkmark', Checkmark),
  CheckmarkRounded: iconWrapper('CheckmarkRounded', CheckmarkRounded),
  ChevronThick: iconWrapper('ChevronThick', ChevronThick),
  ChevronThinDown: iconWrapper('ChevronThinDown', ChevronThinDown),
  // just rotate ChevronThinDown to get other chevrons
  ChevronThinUp: iconWrapper('ChevronThinUp', iconWrapper('ChevronThinDown', ChevronThinDown)),
  ChevronThinLeft: iconWrapper('ChevronThinLeft', iconWrapper('ChevronThinDown', ChevronThinDown)),
  ChevronThinRight: iconWrapper(
    'ChevronThinRight',
    iconWrapper('ChevronThinDown', ChevronThinDown)
  ),
  ChevronThickUp: iconWrapper('ChevronThickUp', iconWrapper('ChevronThick', ChevronThick)),
  ChevronThickDown: iconWrapper('ChevronThickDown', iconWrapper('ChevronThick', ChevronThick)),
  ChevronThickLeft: iconWrapper('ChevronThickLeft', iconWrapper('ChevronThick', ChevronThick)),
  Close: iconWrapper('Close', Close),
  CloseCircle: iconWrapper('CloseCircle', CloseCircle),
  CloseOutline: iconWrapper('CloseOutline', CloseOutline),
  CollectionFilled: iconWrapper('CollectionFilled', CollectionFilled),
  Collections: iconWrapper('Collections', Collections),
  Copy: iconWrapper('Copy', Copy),
  DocumentFilled: iconWrapper('DocumentFilled', DocumentFilled),
  Download: iconWrapper('Download', Download),
  Edit: iconWrapper('Edit', Edit),
  EmptyCollections: iconWrapper('EmptyCollections', EmptyCollections),
  EmptyNotifications: iconWrapper('EmptyNotifications', EmptyNotifications),
  Exclamation: iconWrapper('Exclamation', Exclamation),
  Folder: iconWrapper('Folder', Folder),
  Gear: iconWrapper('Gear', Gear),
  Home: iconWrapper('Home', Home),
  HomeFilled: iconWrapper('HomeFilled', HomeFilled),
  Integrations: iconWrapper('Integrations', Integrations),
  IntegrationsFilled: iconWrapper('IntegrationsFilled', IntegrationsFilled),
  List: iconWrapper('List', List),
  ListFilled: iconWrapper('ListFilled', ListFilled),
  ListFilled16: iconWrapper('ListFilled16', ListFilled16),
  ListSave: iconWrapper('ListSave', ListSave),
  LockClosed: iconWrapper('LockClosed', LockClosed),
  LockOpen: iconWrapper('LockOpen', LockOpen),
  Member: iconWrapper('Member', Member),
  Members: iconWrapper('Members', Members),
  Menu: iconWrapper('Menu', Menu),
  MenuClose: iconWrapper('MenuClose', MenuClose),
  MyDocuments: iconWrapper('MyDocuments', MyDocuments),
  OpenAll: iconWrapper('OpenAll', OpenAll),
  PeopleList: iconWrapper('PeopleList', PeopleList),
  Pin: iconWrapper('Pin', Pin),
  Plus: iconWrapper('Plus', Plus),
  PlusBox: iconWrapper('PlusBox', PlusBox),
  PlusBoxFilled: iconWrapper('PlusBoxFilled', PlusBoxFilled),
  PlusCircle: iconWrapper('PlusCircle', PlusCircle),
  PlusCircleActive: iconWrapper('PlusCircleActive', iconWrapper('PlusCircle', PlusCircle)),
  PlusCircleBlue: iconWrapper('PlusCircleBlue', iconWrapper('PlusCircle', PlusCircle)),
  PlusCircleChecked: iconWrapper('PlusCircleChecked', PlusCircleChecked),
  PlusCircleDark: iconWrapper('PlusCircleDark', PlusCircleDark),
  ProfileAvatar: iconWrapper('ProfileAvatar', ProfileAvatar),
  Search: iconWrapper('Search', Search),
  Share: iconWrapper('Share', Share),
  ShareFilled: iconWrapper('ShareFilled', ShareFilled),
  StarFilled: iconWrapper('StarFilled', StarFilled),
  Tabs: iconWrapper('Tabs', Tabs),
  Trash: iconWrapper('Trash', Trash),
  TriangleDown: iconWrapper('TriangleDown', TriangleDown),
  UserSwitch: iconWrapper('UserSwitch', UserSwitch),
  WorkingDoc: iconWrapper('WorkingDoc', WorkingDoc),
};

export default icons;
